import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router-dom';
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";
import PlatformRatings from "./PlatformRatings";
import { ActionIcon, Button, Loader, Menu, Paper, Stack, Text } from "@mantine/core";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import Tutorial from './Tutorial';
import { RatingPillsStack } from './ratingPills';

const Notifications = ( {unreadCountCallback} ) => {
  const myUser = useContext(MyUserContext);

  const [notifications, setNotifications] = useState(null);
  const [unreadCount, setUnreadCount] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkMarking, setBulkMarking] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  // ID of the most-recently-toggled unread card; drives placement of
  // the floating "Отметить выделенные..." pill so it appears next to
  // the card the user just interacted with (see effect below).
  const [lastToggledNtfId, setLastToggledNtfId] = useState(null);
  // Viewport-anchored position for the bulk-mark pill: pinned close
  // to the checkmark of the last-toggled card.
  const [bulkPillPos, setBulkPillPos] = useState(null);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const removing = next.has(id);
      if (removing) next.delete(id); else next.add(id);
      setLastToggledNtfId((cur) => {
        if (!removing) return id; // selecting → anchor to this card
        if (cur !== id) return cur; // unselecting a different card → keep anchor
        // Unselected the current anchor. Move to any remaining
        // selected id, or clear.
        const remaining = next.values().next().value;
        return remaining ?? null;
      });
      return next;
    });
  }, []);

  // Recompute pill placement whenever the anchor changes, on scroll,
  // or on resize. Anchors to the CHECKMARK inside the card (not the
  // card as a whole) so the pill lands right next to the control the
  // user just tapped. Falls back to the fixed bottom-right corner if
  // the anchor element is missing.
  useEffect(() => {
    if (!lastToggledNtfId || selectedIds.size === 0) {
      setBulkPillPos(null);
      return undefined;
    }
    // Rough width of the pill so we can decide whether it fits to the
    // right of the checkmark on desktop. Slight overestimate is safer
    // than clipping. Recomputed on every layout pass so a very long
    // count doesn't slip past.
    const PILL_ESTIMATED_WIDTH = 420;
    const isDesktop = typeof window !== 'undefined'
      && window.matchMedia('(min-width: 768px)').matches;
    const compute = () => {
      const card = document.querySelector(`[data-notification-id="${lastToggledNtfId}"]`);
      const check = card && card.querySelector('[data-ntf-checkbox]');
      const anchor = check || card;
      if (!anchor) { setBulkPillPos(null); return; }
      const rect = anchor.getBoundingClientRect();
      // Desktop: prefer to the right of the checkmark on the same
      // baseline. If that would blow past the viewport, fall back to
      // below-the-checkmark like mobile.
      if (isDesktop) {
        const roomRight = window.innerWidth - rect.right - 16;
        if (roomRight >= PILL_ESTIMATED_WIDTH) {
          setBulkPillPos({ top: rect.top, left: rect.right + 8 });
          return;
        }
      }
      // Below the checkmark, right-edge aligned to it.
      setBulkPillPos({
        top: rect.bottom + 6,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };
    compute();
    window.addEventListener('scroll', compute, { passive: true });
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute);
      window.removeEventListener('resize', compute);
    };
  }, [lastToggledNtfId, selectedIds]);

  const bulkMarkAsRead = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkMarking(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => axiosInstance.post(`/notifications/${id}/markAsRead`))
      );
      const marked = new Set(
        ids.filter((_, i) => results[i].status === 'fulfilled')
      );
      if (marked.size === 0) return;
      setNotifications((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((n) => (
          n?.notification && marked.has(n.notification.id)
            ? { ...n, notification: { ...n.notification, read: true } }
            : n
        ));
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        marked.forEach((id) => next.delete(id));
        return next;
      });
      // Refresh unread badge to reflect what actually got marked.
      fetchUnreadCount();
    } finally {
      setBulkMarking(false);
    }
  };

  const markAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await axiosInstance.post('/notifications/markAllAsRead');
      // Flip every locally-loaded item to read + clear selection. Server
      // owns truth for unread count; refetch to sync.
      setNotifications((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((n) => (
          n?.notification && n.notification.read === false
            ? { ...n, notification: { ...n.notification, read: true } }
            : n
        ));
      });
      setSelectedIds(new Set());
      fetchUnreadCount();
    } catch (err) {
      console.error('markAllAsRead failed', err);
    } finally {
      setMarkingAll(false);
    }
  };
  const [trendingThumbs, setTrendingThumbs] = useState([]);
  // Ref on the horizontal recs strip so we can convert vertical
  // mouse-wheel input into horizontal scroll + track position.
  const recsStripRef = useRef(null);
  // Scroll-edge flags for the strip. We hide the left chevron when
  // the user is already scrolled all the way left (and the right one
  // when scrolled all the way right) so neither button misleads them
  // about available scroll direction.
  const [recsAtLeft, setRecsAtLeft] = useState(true);
  const [recsAtRight, setRecsAtRight] = useState(true);
  const [nextPageToken, setNextPageToken] = useState(null);
  const nextPageTokenRef = useRef(null);
  const isLoadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    axiosInstance
      .get('/platformRecommendations/thumbnails')
      .then((res) => {
        const items = Array.isArray(res.data) ? res.data : [];
        setTrendingThumbs(items.filter((t) => {
          const source = t.source || (t.version === 2 ? 'ai' : 'similar');
          return source !== 'similar' && source !== 'logbook';
        }));
      })
      .catch(() => {});
  }, []);

  // Convert vertical mouse-wheel to horizontal scroll on the recs
  // strip + maintain the "at left/right edge" flags for chevron
  // visibility. passive:false so preventDefault stops the page from
  // scrolling; only fires when the strip actually has overflow (else
  // let normal page-scroll happen).
  useEffect(() => {
    const el = recsStripRef.current;
    if (!el) return undefined;
    const updateEdges = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setRecsAtLeft(el.scrollLeft <= 1);
      setRecsAtRight(el.scrollLeft >= maxScroll - 1);
    };
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('scroll', updateEdges, { passive: true });
    const ro = new ResizeObserver(updateEdges);
    ro.observe(el);
    updateEdges();
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', updateEdges);
      ro.disconnect();
    };
  }, [trendingThumbs.length]);

  useEffect(() => {
    if (!myUser) return;
    fetchFirstPage();
    // Opening the notifications page = "seen all" in bulk. Clears the
    // sidebar badge immediately; the per-item "unread" styling is
    // independent and keeps letting the user see WHAT was new. Matches
    // FB/IG/VK.
    axiosInstance.post('/notifications/markAllSeen')
      .then(() => {
        setUnreadCount(0);
        unreadCountCallback(0);
      })
      .catch(() => {
        // Fallback: at least refresh from the server if the bulk mark
        // failed, so the UI isn't stuck on a stale number.
        fetchUnreadCount();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myUser]);

  const fetchPage = async (afterToken) => {
    const params = new URLSearchParams();
    params.set('limit', '10');
    if (afterToken) params.set('after', afterToken);
    const res = await axiosInstance.get(`/notifications?${params.toString()}`);
    return res.data || {};
  };

  const fetchFirstPage = async () => {
    try {
      const data = await fetchPage(null);
      const items = Array.isArray(data.items) ? data.items : [];
      setNotifications(items);
      const nextTok = data.nextPageToken || null;
      setNextPageToken(nextTok);
      nextPageTokenRef.current = nextTok;
    } catch (error) {
      console.error(error);
      setNotifications([]);
    }
  };

  // Soft refresh: keep the current list visible (so content shifts down under
  // the spinner instead of blanking) and swap in the fresh first page.
  const softRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchFirstPage();
      fetchUnreadCount();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onRefresh = () => { softRefresh(); };
    window.addEventListener('notifications:refresh', onRefresh);
    return () => window.removeEventListener('notifications:refresh', onRefresh);
  }, [softRefresh]);

  const fetchMore = useCallback(async () => {
    if (isLoadingMoreRef.current) return;
    const token = nextPageTokenRef.current;
    if (!token) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    try {
      const data = await fetchPage(token);
      const items = Array.isArray(data.items) ? data.items : [];
      if (items.length > 0) {
        setNotifications((prev) => (prev || []).concat(items));
      }
      const nextTok = data.nextPageToken || null;
      setNextPageToken(nextTok);
      nextPageTokenRef.current = nextTok;
    } catch (error) {
      console.error(error);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, []);

  // Infinite scroll: auto-fetch the next page when the sentinel scrolls into view.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) fetchMore();
    }, { rootMargin: '400px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [fetchMore, notifications, nextPageToken]);

  const markAsRead = (id) => {
    axiosInstance
    .post(`/notifications/${id}/markAsRead`)
    .then(() => {
      const newUnreadCount = Math.max(0, unreadCount - 1);
      unreadCountCallback(newUnreadCount);
      setUnreadCount(newUnreadCount);
      // Mutate the local item so we don't refetch.
      setNotifications((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((n) => (n.id === id
          ? { ...n, notificationInfo: n.notificationInfo ? { ...n.notificationInfo, notification: { ...n.notificationInfo.notification, read: true } } : n.notificationInfo }
          : n));
      });
    })
    .catch((error) => {
      console.error('Failed to mark notification as read:', error);
    })
  };

  // Auto-mark-as-read on viewport dwell was removed: it marked items
  // read before the user could even register them, and bulk "seen"
  // (fired on page open) now handles the badge counter. "Read" state
  // only flips on explicit click (markAsRead in handleNotificationClick).
  const observeNotification = useCallback(() => {}, []);

  const fetchUnreadCount = () => {
    axiosInstance
    .get('/notifications/unreadCount')
    .then((response) => {
      setUnreadCount(response.data.unreadCount);
      unreadCountCallback(response.data.unreadCount);
    })
    .catch((error) => {
      console.error('Failed to fetch unread notification count:', error);
    })
  };

  // Small icon-key map used by the rating-change block so we don't pull
  // in the whole PlatformRatings picker just for a 16px icon.
  const platformIconSrc = (key) => {
    switch ((key || '').toLowerCase()) {
      case 'imdb': return '/imdb_small_icon.png';
      case 'kinopoisk': return '/kinopoisk_small_icon.png';
      case 'rottentomatoes': return '/rottentomatoes_icon.png';
      case 'rottentomatoesaudience': return '/rottentomatoes_icon.png';
      case 'amazon': return '/amazon_icon.png';
      case 'goodreads': return '/goodreads_icon.png';
      case 'reddit': return '/reddit_icon.png';
      case 'applebooks': return '/applebooks_icon.png';
      case 'steam': return '/steam_icon.png';
      case 'metacritic': return '/metacritic_icon.png';
      case 'metacriticuser': return '/metacritic_icon.png';
      case 'opencritic': return '/opencritic_icon.png';
      case 'opencriticuser': return '/opencritic_icon.png';
      case 'livelib': return '/livelib_icon.png';
      case 'litres': return '/litres_icon.png';
      default: return null;
    }
  };

  const parseStringToBold = (text) => {
    const parts = text.split(/(\*[^*]+\*)/);

    return parts.map((part, index) => {
      if (part.startsWith('*') && part.endsWith('*')) {
        return <strong key={index}>{part.slice(1, -1)}</strong>;
      } else {
        return part;
      }
    });
  };

  const groupNotifications = (notifications) => {
    const GROUP_THRESHOLD = 3;

    const grouped = [];
    let currentGroup = [];

    notifications.forEach((notification, index) => {
      if (
        currentGroup.length > 0 &&
        currentGroup[0].initiatingUserId === notification.initiatingUserId &&
        currentGroup[0].kind === notification.kind
      ) {
        currentGroup.push(notification);
      } else {
        if (currentGroup.length > 0) {
          if (currentGroup.length < GROUP_THRESHOLD) {
            grouped.push(...currentGroup.map((item) => [item]));
          } else {
            grouped.push(currentGroup);
          }
        }
        currentGroup = [notification];
      }
    });

    if (currentGroup.length > 0) {
      if (currentGroup.length < GROUP_THRESHOLD) {
        grouped.push(...currentGroup.map((item) => [item]));
      } else {
        grouped.push(currentGroup);
      }
    }

    return grouped;
  };

  const notificationTitle = (notification) => {
    const baseTitle = notification.title;
    // Trending kinds: backend appends the raw JSON payload to the title,
    // so the message reads "Вышли новые фильмы! {"arts":[...]}" with the
    // blob visible. The arts render as a mini-strip below the title via
    // renderTrendingStrip, so the title just needs to be a clean label.
    if (notification.kind === 'newTrendingMovies') {
      return language === 'ru' ? 'Вышли новые фильмы!' : 'New movies out!';
    }
    if (notification.kind === 'newTrendingBooks') {
      return language === 'ru' ? 'Вышли новые книги!' : 'New books out!';
    }
    if (notification.kind === 'newTrendingGames') {
      return language === 'ru' ? 'Вышли новые игры!' : 'New games out!';
    }
    // "SomeonesChat" flavor: backend sends a generic "в чьем-то чате"
    // title, but the read-side handler attaches owner user info. Swap
    // in "в чате *{ownerName}*" so parseStringToBold renders the name
    // bold. Owner avatar is rendered separately by the caller.
    if (notification.kind === "user.wrote.something.inSomeonesChat" && notification.ownerUserName) {
      const owner = notification.ownerUserName;
      return language === "ru"
        ? `написал(а) сообщение в чате *${owner}*`
        : `wrote a message in *${owner}*'s chat`;
    }
    const isFinished = notification.notification && notification.notification.finished === true;
    if (!isFinished) {
      return baseTitle;
    }
    if (notification.kind === "user.watched.something") {
      return language === "ru" ? "посмотрел(а) фильм" : "finished the movie";
    }
    if (notification.kind === "user.read.something") {
      return language === "ru" ? "прочитал(а) книгу" : "finished the book";
    }
    if (notification.kind === "user.played.something") {
      return language === "ru" ? "прошел(а) игру" : "finished the game";
    }
    return baseTitle;
  };

  const NotificationCard = ({ notificationGroup }) => {
    const navigate = useNavigate();
    const [isExpanded, setIsExpanded] = useState(false);

    const firstNotification = notificationGroup[0];
    const ntfText = notificationTitle(firstNotification);
    const link = firstNotification.linkToEvent;

    const handleNotificationClick = (notification) => {
      markAsRead(notification.notification.id);
      // Chat is collapsed by default on /artEvents/:id. Exception: when
      // this notification is about someone writing in the art event's
      // chat, the user clearly wants to see the chat — pass a flag
      // through router state so the page opens the chat overlay.
      const kind = notification.kind || notification?.notification?.textCode;
      const chatMessageKinds = new Set([
        'user.wrote.something.inYourChat',
        'user.wrote.something.inTheirChat',
        'user.wrote.something.inSomeonesChat',
        'user.wroteInRecommendation.something',
      ]);
      const showChat = chatMessageKinds.has(kind);
      navigate(notification.linkToEvent, showChat ? { state: { showChat: true } } : undefined);
    };

    const localizedShowMore = (count) => {
      return language === "ru" ? `Показать еще ${count}` : `Show ${count} more`;
    };

    const localizedHide = () => {
      return language === "ru" ? "Скрыть" : "Hide";
    };

    const renderEventTags = (tags) => {
      if (!tags || tags.length === 0) {
        return null;
      }
      // Drop auto-derived families (genre=5, subCategory=6, year=7) —
      // they're noise in the notifications feed.
      const visible = tags.filter((t) => t.type !== 5 && t.type !== 6 && t.type !== 7);
      if (visible.length === 0) {
        return null;
      }
      return (
        <div className="mt-1 flex flex-wrap gap-1">
          {visible.map((tag) => (
            <TagBadge
              key={tag.id}
              size="xs"
              radius="sm"
              variant="filled"
              color={tagBadgeColor(tag.name, tag.type)}
            >
              {tagName(tag.name, language)}
            </TagBadge>
          ))}
        </div>
      );
    };

    const renderEventMessages = (messages) => {
      if (!messages || messages.length === 0) {
        return null;
      }

      return (
        <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          <Text size="xs" tt="uppercase" c="dimmed" mb={4} style={{ letterSpacing: '0.05em' }}>
            {language === "ru" ? "Сообщения из чата" : "Chat messages"}
          </Text>
          <div className="flex flex-col gap-2">
          {[...messages].reverse().map((message, index) => {
            const trimmedText = (message.text || '').trim();
            const imageCount = Array.isArray(message.imageUrls) ? message.imageUrls.length : 0;
            // If a message is image-only we'd render an empty bubble
            // otherwise. Substitute a "📷 Image" (or "N images") stub
            // so the recipient sees something happened.
            let bodyText = trimmedText;
            if (!bodyText && imageCount > 0) {
              const noun = language === 'ru'
                ? (imageCount === 1 ? 'изображение' : `${imageCount} изображений`)
                : (imageCount === 1 ? 'image' : `${imageCount} images`);
              bodyText = `📷 ${noun}`;
            }
            return (
              <div key={index} className="flex items-start gap-2">
                <img className="w-6 h-6 rounded-full" src={message.userAvatar} />
                <div className="flex-1 leading-snug">
                  <Text span size="xs" fw={600} mr={4}>{message.userName}</Text>
                  <Text span size="xs" c="dimmed">{bodyText}</Text>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      );
    };

    const renderArtTitle = (notification) => {
      if (!notification.artTitle) {
        return null;
      }
      const yearPart = notification.artYear ? ` (${notification.artYear})` : "";
      return (
        <Text size="sm" fw={600}>
          {notification.artTitle}
          {yearPart}
        </Text>
      );
    };

    const renderLastLogbookTime = (notification) => {
      if (!notification.lastLogbookTime || !notification.lastLogbookLabel) {
        return null;
      }
      const formatted = new Date(notification.lastLogbookTime).toLocaleDateString(language, {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      return (
        <Text size="xs" c="dimmed" mt={2}>
          {notification.lastLogbookLabel}: {formatted}
        </Text>
      );
    };

    const renderRatingChange = (notification) => {
      if (notification.kind !== 'art.rating.changed') return null;
      const raw = notification?.notification?.textPayload;
      if (!raw) return null;
      let parsed;
      try { parsed = JSON.parse(raw); } catch { return null; }
      // Payload is normally an array of change entries; accept the
      // pre-merge single-object shape too for backward compatibility.
      const changes = Array.isArray(parsed) ? parsed : [parsed];
      if (changes.length === 0) return null;
      // fmtRating / fmtVotes render `-` when the OLD side was missing
      // (0 or nil) so a "brand new" score reads as "оценка: - → 4.8"
      // instead of the misleading "было 0 стало 4.8".
      const fmtRating = (v) => {
        if (v === null || v === undefined || v === 0) return '-';
        return Number.isInteger(v) ? v.toString() : v.toFixed(1);
      };
      const fmtVotes = (v) => {
        if (v === null || v === undefined || v === 0) return '-';
        return v.toLocaleString();
      };
      const ratingLabel = language === 'ru' ? 'оценка' : 'rating';
      const votesLabel = language === 'ru' ? 'отзывы' : 'reviews';
      const variantLabelFor = (key) => {
        switch (key) {
          case 'rottenTomatoes': return language === 'ru' ? 'критики' : 'critics';
          case 'rottenTomatoesAudience': return language === 'ru' ? 'зрители' : 'audience';
          case 'metacritic': return language === 'ru' ? 'критики' : 'critics';
          case 'metacriticUser': return language === 'ru' ? 'пользователи' : 'users';
          case 'openCritic': return language === 'ru' ? 'критики' : 'critics';
          case 'openCriticUser': return language === 'ru' ? 'игроки' : 'players';
          default: return null;
        }
      };
      return (
        <div className="mt-2 flex flex-col gap-1">
          {changes.map((p, idx) => {
            const iconSrc = platformIconSrc(p.p);
            const oldR = typeof p.or === 'number' ? p.or : null;
            const newR = typeof p.nr === 'number' ? p.nr : null;
            const oldV = typeof p.ov === 'number' ? p.ov : null;
            const newV = typeof p.nv === 'number' ? p.nv : null;
            const variantLabel = variantLabelFor(p.p);
            const ratingChanged = oldR !== null && newR !== null && oldR !== newR;
            const votesChanged = oldV !== null && newV !== null && oldV !== newV;
            return (
              <div key={p.p + ':' + idx} className="flex items-center gap-2 flex-wrap">
                {iconSrc && (
                  <img src={iconSrc} alt={p.p} className="w-4 h-4 rounded-sm flex-shrink-0" />
                )}
                {variantLabel && (
                  <Text size="xs" style={{ color: 'var(--mantine-color-dimmed)' }}>
                    ({variantLabel})
                  </Text>
                )}
                {ratingChanged && (
                  <Text size="xs" style={{ color: 'var(--mantine-color-dimmed)' }}>
                    {ratingLabel}: {fmtRating(oldR)} → {fmtRating(newR)}
                  </Text>
                )}
                {votesChanged && (
                  <Text size="xs" style={{ color: 'var(--mantine-color-dimmed)' }}>
                    {votesLabel}: {fmtVotes(oldV)} → {fmtVotes(newV)}
                  </Text>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    const renderDescriptionPreview = (description) => {
      if (!description) {
        return null;
      }
      const paragraphs = description.split(/\r?\n/);
      const firstParagraph = paragraphs.find((p) => p.trim() !== "");
      if (!firstParagraph) {
        return null;
      }
      const sentences = firstParagraph.match(/[^.!?]+[.!?]+/g) || [firstParagraph];
      const preview = sentences.join(" ").trim();
      const limit = 150;
      const trimmed = preview.length > limit ? preview.slice(0, limit).trim() + "..." : preview;

      return (
        <Text size="xs" c="dimmed" mt={2}>
          {trimmed}
        </Text>
      );
    };

    // Trending notifications (newTrendingMovies/Books/Games) carry a
    // small array of fresh arts in notification.textPayload — see
    // hotartnotify.TrendingPayload. Render it as a horizontal mini-strip
    // of posters inside the card so the user sees WHAT trended, not just
    // "something did".
    const TRENDING_KINDS = new Set(['newTrendingMovies', 'newTrendingBooks', 'newTrendingGames']);
    const renderTrendingStrip = (notification) => {
      if (!TRENDING_KINDS.has(notification.kind)) return null;
      const raw = notification?.notification?.textPayload;
      if (!raw) return null;
      let payload;
      try { payload = JSON.parse(raw); } catch { return null; }
      const arts = Array.isArray(payload?.arts) ? payload.arts : [];
      if (arts.length === 0) return null;
      return (
        <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar pb-1" style={{ scrollbarWidth: 'none' }}>
          {arts.map((a) => {
            const displayName = (language === 'ru' && a.ruName) ? a.ruName : (a.name || '');
            return (
              <Link
                key={a.id}
                to={`/arts/${a.id}`}
                className="flex-shrink-0 block w-16 group"
                data-ntf-child-clickable=""
                onClick={(e) => e.stopPropagation()}
                title={a.year ? `${displayName} (${a.year})` : displayName}
              >
                <img
                  src={a.thumb}
                  alt={displayName}
                  className="w-16 h-24 object-cover rounded-md shadow-sm transition-transform group-hover:-translate-y-0.5 group-hover:shadow-md"
                  style={{ backgroundColor: 'var(--mantine-color-default)' }}
                />
                <Text size="xs" c="dimmed" lineClamp={1} mt={1}>
                  {displayName}
                </Text>
              </Link>
            );
          })}
        </div>
      );
    };

    const renderSingleNotification = (notification, isFirst) => {
      const isUnread = notification.notification.read === false;
      const inSelectionMode = selectedIds.size > 0;
      const text = isFirst ? ntfText : notificationTitle(notification);
      const boldText = notification.initiatingUserName
        ? "*" + notification.initiatingUserName + "* " + text
        : "*" + text + "*";
      const ntfId = notification.notification.id;
      const isSelected = isUnread && selectedIds.has(ntfId);

      return (
        <Paper
          ref={isUnread ? observeNotification : undefined}
          data-notification-id={isUnread ? ntfId : undefined}
          shadow={isUnread ? "sm" : "xs"}
          radius="lg"
          p="sm"
          className="group cursor-pointer transition-all duration-150 hover:shadow-lg"
          style={{ position: 'relative', backgroundColor: isUnread ? 'var(--app-unread-bg)' : 'var(--app-surface)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--app-surface-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isUnread ? 'var(--app-unread-bg)' : 'var(--app-surface)'; }}
          onClick={() => handleNotificationClick(notification)}
        >
          {isUnread && (
            <ActionIcon
              variant={isSelected ? "filled" : "default"}
              color="indigo"
              radius="xl"
              size="sm"
              data-ntf-checkbox=""
              // Visibility rules:
              //   - Selected: always visible (filled state).
              //   - Any other card selected (inSelectionMode): visible on
              //     every unread card so bulk-select is one-tap per card.
              //   - Otherwise: hidden; revealed on hover (desktop) or
              //     touch-down (:active on mobile). pointer-events-none
              //     when hidden so it can't swallow taps meant for the
              //     card body.
              className={
                isSelected || inSelectionMode
                  ? ""
                  : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-active:opacity-100 group-active:pointer-events-auto transition-opacity"
              }
              style={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}
              onClick={(e) => { e.stopPropagation(); toggleSelected(ntfId); }}
              aria-label={isSelected ? "Deselect notification" : "Select notification"}
            >
              {isSelected ? <IconCheck size={14} stroke={3} /> : null}
            </ActionIcon>
          )}
          <div className="flex gap-3 items-start">
            <div className="flex-shrink-0">
              {(() => {
                const artLink = notification.linkToArt || (notification.notification?.artEventId && `/artEvents/${notification.notification.artEventId}`);
                const interactive = !!artLink;
                return (
                  <img
                    className="rounded-lg w-[72px] h-[96px] object-cover"
                    src={notification.artImageUrl}
                    data-ntf-child-clickable={interactive ? '' : undefined}
                    onClick={interactive ? (e) => { e.stopPropagation(); navigate(artLink); } : undefined}
                    style={interactive
                      ? { cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s' }
                      : undefined}
                    onMouseEnter={interactive ? (e) => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.1)'; } : undefined}
                    onMouseLeave={interactive ? (e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; } : undefined}
                  />
                );
              })()}
              {notification.otherPlatformInfos && notification.otherPlatformInfos.length > 0 && (() => {
                const visibleCount = notification.otherPlatformInfos.filter(p => p.score !== undefined && p.score !== 0).length;
                const twoColumns = visibleCount > 2;
                return (
                  <div className="mt-1">
                    <PlatformRatings art={{ otherPlatformInfos: notification.otherPlatformInfos }} center={!twoColumns} twoColumns={twoColumns} hidePairVotes={true} />
                  </div>
                );
              })()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {notification.initiatingUserName ? (
                  <>
                    <img
                      className="w-8 h-8 rounded-full flex-shrink-0"
                      src={notification.initiatingUserAvatarUrl}
                      data-ntf-child-clickable=""
                      onClick={(e) => {
                        e.stopPropagation();
                        if (notification.initiatingUserId) {
                          navigate(`/users/${notification.initiatingUserId}/likes`);
                        }
                      }}
                      style={{ cursor: 'pointer', transition: 'transform 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                    />
                    <Text size="sm">
                      {parseStringToBold(boldText)}
                      {notification.kind === "user.wrote.something.inSomeonesChat" && notification.ownerUserAvatarUrl && (
                        <img
                          src={notification.ownerUserAvatarUrl}
                          alt=""
                          className="inline-block w-5 h-5 rounded-full align-middle ml-1"
                          style={{ objectFit: 'cover', cursor: notification.ownerUserId ? 'pointer' : undefined }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (notification.ownerUserId) navigate(`/users/${notification.ownerUserId}/likes`);
                          }}
                        />
                      )}
                    </Text>
                  </>
                ) : (
                  <Text size="sm">{parseStringToBold(boldText)}</Text>
                )}
              </div>
              {renderArtTitle(notification)}
              {renderRatingChange(notification)}
              {renderTrendingStrip(notification)}
              {renderDescriptionPreview(notification.artDescription)}
              {renderLastLogbookTime(notification)}
              {renderEventTags(notification.eventTags)}
              {renderEventMessages(notification.eventMessages)}
            </div>
          </div>
        </Paper>
      );
    };

    return (
      <div>
        {renderSingleNotification(firstNotification, true)}
        {notificationGroup.length > 1 && (
          <div className="ml-4 mt-1">
            <Button variant="subtle" size="xs" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? localizedHide() : localizedShowMore(notificationGroup.length - 1)}
            </Button>
            {isExpanded && (
              <Stack gap="xs" mt="xs">
                {notificationGroup.slice(1).map((notification) => (
                  <div key={notification.notification.id}>
                    {renderSingleNotification(notification, false)}
                  </div>
                ))}
              </Stack>
            )}
          </div>
        )}
      </div>
    );
  };

  const language = myUser ? myUser.myUserSettings.lang : 'ru';

  if (!myUser) {
    return (
      <div className="flex mt-2 md:mt-4 justify-center items-center text-center">
        <Text c="dimmed">
          {language === "ru" ? "Здесь будут уведомления" : "Here will be notifications"}
        </Text>
      </div>
    );
  }

  if (notifications === null || unreadCount === null) {
    return (
      <div></div>
    )
  }

  const groupedNotifications = notifications.map((notification) => [notification]);

  const showTutorial = myUser?.myUserInfo?.finishedTutorial === false;

  return (
    // overflow-x-hidden used to live on this container; it clipped the
    // trending strip's intentional lg:w-[150%] lg:-mx-[25%] breakout
    // (first poster sliced, right scroll chevron masked off-canvas).
    // The strip has its own overflow-x-auto, so page-level overflow
    // stays bounded.
    <div className="container mx-auto w-full lg:w-1/2">
      {trendingThumbs.length > 0 && myUser?.myUserInfo?.myUserId && (() => {
        // Poster size matches the ntfs card's art thumbnail (72×96)
        // so the rail reads as the same visual object type as what
        // appears next to each notification below.
        const POSTER_W = 72;
        const POSTER_H = 96;
        // Count distinct sources in the current rail; a per-tile source
        // badge only earns its place when the rail actually mixes sources.
        // When everything is one source (common for 'friends' feeds) the
        // badge becomes decorative noise; one small header label up top
        // tells the user what they're looking at and leaves the posters
        // clean.
        const srcOf = (t) => t.source || (t.version === 2 ? 'ai' : 'similar');
        const sourcesSet = new Set(trendingThumbs.map(srcOf));
        const uniformSource = sourcesSet.size === 1 ? [...sourcesSet][0] : null;
        const uniformLabel = uniformSource && (
          uniformSource === 'ai' ? (language === 'ru' ? 'ИИ советует' : 'AI picks')
            : uniformSource === 'friends' ? (language === 'ru' ? 'Друзья советуют' : 'From friends')
            : uniformSource === 'logbook' ? (language === 'ru' ? 'По вашему журналу' : 'From your logbook')
            : uniformSource === 'topTodos' ? (language === 'ru' ? 'Топ планов' : 'Top plans')
            : (language === 'ru' ? 'Рекомендации для вас' : 'Recommendations for you'));
        const headerLabel = uniformLabel || (language === 'ru' ? 'Рекомендации для вас' : 'Recommendations for you');
        return (
          // Breakout wrapper carries BOTH the header and the strip —
          // so the title aligns with the first poster and "All →" with
          // the last, instead of hugging a narrower column that the
          // posters visibly overhang.
          <div className="mb-4 lg:w-[150%] lg:-mx-[25%]">
            <div className="flex items-center justify-between mb-2 px-2">
              <Text size="sm" fw={600} style={{ color: 'var(--mantine-color-text)' }}>
                {headerLabel}
              </Text>
              <Link
                to={`/users/${myUser.myUserInfo.myUserId}/recommendations`}
                style={{ color: 'var(--mantine-color-anchor)', textDecoration: 'none', fontSize: 13, fontWeight: 500 }}
              >
                {language === 'ru' ? 'Все →' : 'All →'}
              </Link>
            </div>
            <div className="relative">
            <div
              ref={recsStripRef}
              className="recs-strip flex items-start gap-3 py-1 px-2 overflow-x-auto overflow-y-hidden no-scrollbar min-w-0 max-w-full"
              style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' }}
            >
              {trendingThumbs.map((t) => {
                const source = srcOf(t);
                let tagLabel, tagColor;
                switch (source) {
                  case 'ai':
                    tagLabel = 'AI';
                    tagColor = 'var(--mantine-color-violet-filled)';
                    break;
                  case 'friends':
                    tagLabel = language === 'ru' ? 'Друзья' : 'Friends';
                    tagColor = 'var(--mantine-color-pink-filled)';
                    break;
                  case 'logbook':
                    tagLabel = language === 'ru' ? 'Журнал' : 'Logbook';
                    tagColor = 'var(--mantine-color-teal-filled)';
                    break;
                  case 'topTodos':
                    tagLabel = language === 'ru' ? 'Планы' : 'Plans';
                    tagColor = 'var(--mantine-color-orange-filled)';
                    break;
                  default:
                    tagLabel = language === 'ru' ? 'Похожее' : 'Similar';
                    tagColor = 'var(--mantine-color-blue-filled)';
                }
                const showFriendAvatar = source === 'friends' && t.fromUserAvatarUrl;
                const otherCount = (source === 'friends' && t.otherRecommendersCount) || 0;
                const displayName = (language === 'ru' && t.ruName) ? t.ruName : (t.name || '');
                // Platform ratings pills need otherPlatformInfos on the
                // item. The thumbnails endpoint returns ArtLite-shaped
                // data; the field is present when the backend fills it.
                const artForPills = { ...t, id: t.artId, category: t.category };
                return (
                  <Link
                    key={t.artId}
                    to={`/arts/${t.artId}`}
                    className="flex-shrink-0 block group"
                    style={{ width: POSTER_W }}
                    title={displayName}
                  >
                    <div
                      className="rounded-lg overflow-hidden shadow-sm transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:shadow-md"
                      style={{ position: 'relative', width: POSTER_W, height: POSTER_H, background: 'var(--mantine-color-default)' }}
                    >
                      <img
                        src={t.thumbnailUrl}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                      {/* Only render the per-tile source pill when the rail
                          actually mixes sources — otherwise the header
                          label carries the signal. */}
                      {!uniformSource && (
                        <span
                          className="absolute px-1.5 rounded text-white text-[10px] font-semibold"
                          style={{
                            top: 6, left: 6,
                            background: tagColor,
                            lineHeight: "14px",
                            boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                          }}
                        >
                          {tagLabel}
                        </span>
                      )}
                      {/* Multi-platform rating pills — same shared stack as
                          the logged-out hero and the Similar grid. */}
                      <RatingPillsStack art={artForPills} max={3} />
                      {showFriendAvatar && (
                        <img
                          src={t.fromUserAvatarUrl}
                          alt={t.fromUserName || ''}
                          title={t.fromUserName || ''}
                          className="rounded-full object-cover"
                          style={{
                            position: 'absolute',
                            right: 6, bottom: 6,
                            width: 24, height: 24,
                            border: '1.5px solid #fff',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
                          }}
                        />
                      )}
                      {showFriendAvatar && otherCount > 0 && (
                        <span
                          className="rounded-full text-white text-[9px] font-semibold flex items-center justify-center"
                          style={{
                            position: 'absolute',
                            left: 6, bottom: 6,
                            minWidth: 20, height: 20,
                            padding: '0 4px',
                            background: 'var(--mantine-color-pink-filled)',
                            border: '1.5px solid #fff',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
                            lineHeight: 1,
                          }}
                        >
                          +{otherCount}
                        </span>
                      )}
                    </div>
                    {displayName && (
                      <Text size="xs" lineClamp={1} mt={4} style={{ color: 'var(--mantine-color-text)' }}>
                        {t.year ? `${displayName} · ${t.year}` : displayName}
                      </Text>
                    )}
                  </Link>
                );
              })}
            </div>
            {/* Left + right scroll chevrons for mouse-only users.
                Hidden on mobile (touch devices handle swipe natively)
                AND hidden at the respective edge so a chevron never
                points nowhere. Scrolls ~85% of strip width per click. */}
            {!recsAtLeft && (
            <button
              type="button"
              aria-label={language === 'ru' ? 'Прокрутить влево' : 'Scroll left'}
              onClick={() => {
                const el = recsStripRef.current;
                if (!el) return;
                el.scrollBy({ left: -Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
              }}
              className="hidden md:flex absolute items-center justify-center rounded-full"
              style={{
                top: '50%', left: 4,
                transform: 'translateY(-50%)',
                width: 36, height: 36,
                background: 'color-mix(in srgb, var(--mantine-color-body) 85%, transparent)',
                color: 'var(--mantine-color-text)',
                border: '1px solid var(--mantine-color-default-border)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>
            )}
            {!recsAtRight && (
            <button
              type="button"
              aria-label={language === 'ru' ? 'Прокрутить вправо' : 'Scroll right'}
              onClick={() => {
                const el = recsStripRef.current;
                if (!el) return;
                el.scrollBy({ left: Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
              }}
              className="hidden md:flex absolute items-center justify-center rounded-full"
              style={{
                top: '50%', right: 4,
                transform: 'translateY(-50%)',
                width: 36, height: 36,
                background: 'color-mix(in srgb, var(--mantine-color-body) 85%, transparent)',
                color: 'var(--mantine-color-text)',
                border: '1px solid var(--mantine-color-default-border)',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
            )}
            </div>
          </div>
        );
      })()}
      {showTutorial && <Tutorial language={language} />}
      {isRefreshing && (
        <div
          className="flex justify-center items-center"
          style={{ height: 48, overflow: 'hidden' }}
        >
          <Loader size="sm" />
        </div>
      )}
      {
      (groupedNotifications && groupedNotifications.length == 0) &&
      <div className="flex mt-4 justify-center items-center text-center">
        <Text c="dimmed" ta="center">
          {language === "ru"
            ? <>У вас пока нет уведомлений.<br/>Подписывайтесь на других пользователей, чтобы получать их.</>
            : <>No notifications yet.<br/>Subscribe to other users to receive notifications.</>
          }
        </Text>
      </div>
      }

      {
      (groupedNotifications && groupedNotifications.length > 0) &&
      <Stack gap="sm" mt="sm">
        {groupedNotifications.map((notificationGroup, index) => (
          <div key={index}>
            <NotificationCard notificationGroup={notificationGroup}/>
          </div>
        ))}
      </Stack>
      }
      {isLoadingMore && (
        <div className="flex justify-center mt-4">
          <Loader size="sm" />
        </div>
      )}
      {nextPageToken && (
        <div ref={sentinelRef} style={{ height: 1 }} />
      )}
      {selectedIds.size > 0 && (
        <Button.Group
          style={{
            position: 'fixed',
            // Anchor to the last-toggled card's CHECKMARK. Desktop
            // lands the pill immediately to the right of the checkmark
            // (bulkPillPos.left is set). If it wouldn't fit there or
            // we're on mobile, it drops in below the checkmark
            // (bulkPillPos.right is set). Falls back to the
            // bottom-right corner when the anchor isn't in the DOM.
            ...(bulkPillPos
              ? {
                  top: bulkPillPos.top,
                  ...(bulkPillPos.left != null
                    ? { left: bulkPillPos.left }
                    : { right: bulkPillPos.right }),
                }
              : { bottom: 88, right: 24 }),
            zIndex: 200,
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
            borderRadius: 999,
          }}
        >
          <Button
            color="indigo"
            radius="xl"
            size="md"
            loading={bulkMarking}
            onClick={bulkMarkAsRead}
          >
            {language === "ru"
              ? `Отметить как прочитанное (${selectedIds.size})`
              : `Mark as read (${selectedIds.size})`}
          </Button>
          <Menu position="top-end" withinPortal>
            <Menu.Target>
              <Button
                color="indigo"
                radius="xl"
                size="md"
                px={10}
                aria-label={language === "ru" ? "Ещё" : "More"}
              >
                <IconChevronDown size={18} stroke={2.5} />
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={markAllAsRead}
                disabled={markingAll}
              >
                {language === "ru"
                  ? "Отметить все как прочитанное"
                  : "Mark all as read"}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Button.Group>
      )}
    </div>
  );
};

export default Notifications;
